/*!
 * Copyright © 2025. Verizon Connect Ireland Limited. All rights reserved.
 */

/**
 * Removes Angular router outlet parameters from a URL string.
 * @param outlet - The URL string that may contain outlet parameters
 * @returns The URL string with outlet parameters removed
 */
export function trimOutlet(outlet: string): string {
  if (!outlet) {
    return '';
  }
  // Remove outlet parameters like (param1:value1)
  return outlet.replace(/\([^)]*:[^)]*\)/g, '');
}

/**
 * Appends a path segment to a URL, optionally preserving query parameters.
 * Angular router outlet parameters are automatically removed from the URL.
 * @param currentUrl - The current URL to modify
 * @param newSegment - The new segment to append to the path
 * @param preserveQuery - Whether to preserve query parameters (default: true)
 * @returns The modified URL with the new segment appended and outlet parameters removed
 */
export function appendPathSegment(currentUrl: string, newSegment: string, preserveQuery: boolean = true): string {
  const cleanUrl = trimOutlet(currentUrl);
  const isAbsolute = cleanUrl.includes('://');
  const hasLeadingSlash = cleanUrl.startsWith('/');
  const url = new URL(cleanUrl, window.location.origin);
  url.pathname = url.pathname.replace(/\/+$/, '') + '/' + newSegment;

  if (!preserveQuery) {
    url.search = '';
  }

  if (isAbsolute) {
    return url.toString();
  } else {
    const result = url.pathname + url.search + url.hash;
    return hasLeadingSlash ? result : result.substring(1);
  }
}
